import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import type { SortingStrategy } from '@dnd-kit/sortable';
import {
  Bars3Icon,
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { PinIcon, PinLeftIcon, PinRightIcon } from '@/components/ui/icons/PinIcons';
import { ContextMenu, useContextMenu } from '@/components/ui/ContextMenu';
import { useUIStore } from '@/stores/ui/ui-store';
import { clsx } from 'clsx';
import type { CrossDndState } from './createCrossDndProvider';

// No-op strategy: items do not displace during drag.
const noDisplacementStrategy: SortingStrategy = () => null;

type ColumnConfig = { id: string; label: string; visible: boolean };
type ColumnPinSide = 'left' | 'right' | null;

/** Store interface required by the column manager panel. */
export interface ColumnManagerStore {
  columns: ColumnConfig[];
  pinnedLeft: string[];
  pinnedRight: string[];
  toggleColumnVisibility: (columnId: string) => void;
  showAllColumns: () => void;
  hideAllColumns: () => void;
  resetColumns: () => void;
  isColumnsDefault: () => boolean;
  pinColumn: (columnId: string, side: ColumnPinSide) => void;
  getPinState: (columnId: string) => ColumnPinSide;
}

export interface CreateColumnManagerPanelConfig {
  useColumnStore: {
    <T>(selector: (state: ColumnManagerStore) => T): T;
  };
  useCrossDnd: () => CrossDndState;
  addSidebarPrefix: (id: string) => string;
}

/* ---------- Internal components ---------- */

function createPinButton(useColumnStore: CreateColumnManagerPanelConfig['useColumnStore']) {
  const PinButton: React.FC<{ columnId: string }> = ({ columnId }) => {
    const pinColumn = useColumnStore((s) => s.pinColumn);
    const getPinState = useColumnStore((s) => s.getPinState);
    const currentPin = getPinState(columnId);
    const { open, position, handleContextMenu, close } = useContextMenu();
    return (
      <>
        <button
          type="button"
          onClick={handleContextMenu}
          className={clsx(
            'flex-shrink-0 rounded p-0.5 transition-colors',
            currentPin ? 'text-blue-600 hover:text-blue-800' : 'text-gray-400 hover:text-gray-600'
          )}
          title="Pin column"
          aria-label="Pin column"
        >
          {currentPin === 'left' ? <PinLeftIcon className="h-3.5 w-3.5" /> : currentPin === 'right' ? <PinRightIcon className="h-3.5 w-3.5" /> : <PinIcon className="h-3.5 w-3.5" />}
        </button>
        {open && (
          <ContextMenu
            position={position}
            onClose={close}
            items={[
              {
                label: 'Pin left',
                icon: <PinLeftIcon className="h-3.5 w-3.5" />,
                checked: currentPin === 'left',
                onClick: () => pinColumn(columnId, currentPin === 'left' ? null : 'left'),
              },
              {
                label: 'Pin right',
                icon: <PinRightIcon className="h-3.5 w-3.5" />,
                checked: currentPin === 'right',
                onClick: () => pinColumn(columnId, currentPin === 'right' ? null : 'right'),
              },
              {
                label: 'Unpin',
                dividerBefore: true,
                checked: currentPin === null,
                onClick: () => pinColumn(columnId, null),
              },
            ]}
          />
        )}
      </>
    );
  };
  return PinButton;
}

// eslint-disable-next-line react-refresh/only-export-components -- internal helper component used by the factory below; factory pattern is intentional
const ColumnGroup: React.FC<{
  label: string;
  visible: boolean;
  children: React.ReactNode;
}> = ({ label, children, visible }) => {
  if (!visible) return null;
  return (
    <div className="border-b border-gray-200 pb-2">
      <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
        {label}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
};

/* ---------- Factory ---------- */

export function createColumnManagerPanel(config: CreateColumnManagerPanelConfig) {
  const { useColumnStore, useCrossDnd, addSidebarPrefix: addPrefix } = config;
  const PinButton = createPinButton(useColumnStore);

  const SortableColumnRow: React.FC<{
    id: string;
    label: string;
    visible: boolean;
    onToggleVisibility: (id: string) => void;
    isHidden: boolean;
    scrollContainerRef?: React.RefObject<HTMLDivElement>;
  }> = ({
    id,
    label,
    visible,
    onToggleVisibility,
    isHidden,
    scrollContainerRef,
  }) => {
    const sortableId = addPrefix(id);
    const {
      attributes,
      listeners,
      setNodeRef,
      isDragging,
    } = useSortable({ id: sortableId });
    const { sidebarOverColumnId, activeDragId, activeDragSource } = useCrossDnd();
    const sidebarHighlightColumnId = useUIStore((s) => s.sidebarHighlightColumnId);

    const isTableDragHighlight = sidebarOverColumnId === sortableId && activeDragSource === 'table';
    const isActiveTableDragItem = activeDragSource === 'table' && activeDragId === id;
    const isContextMenuHighlight = sidebarHighlightColumnId === id;

    const scrollRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
      if (isContextMenuHighlight && scrollRef.current) {
        scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, [isContextMenuHighlight]);

    useEffect(() => {
      if (sidebarOverColumnId === sortableId && activeDragSource != null && scrollContainerRef?.current && scrollRef.current) {
        const container = scrollContainerRef.current;
        const el = scrollRef.current;
        const elTop = el.offsetTop;
        const elBottom = elTop + el.offsetHeight;
        const containerTop = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;
        if (elTop < containerTop) {
          container.scrollTop = elTop;
        } else if (elBottom > containerBottom) {
          container.scrollTop = elBottom - container.clientHeight;
        }
      }
    }, [sidebarOverColumnId, sortableId, activeDragSource, scrollContainerRef]);

    const mergedRef = useCallback(
      (node: HTMLDivElement | null) => {
        setNodeRef(node);
        scrollRef.current = node;
      },
      [setNodeRef]
    );

    return (
      <div
        ref={mergedRef}
        data-sortable-id={sortableId}
        className={clsx(
          'relative flex items-center gap-1.5 px-2 py-2 rounded text-sm transition-colors duration-150',
          isContextMenuHighlight ? 'animate-column-highlight-out bg-blue-100' : isActiveTableDragItem ? 'bg-blue-50' : 'bg-white',
          isDragging && 'opacity-0 pointer-events-none',
          isHidden && 'hidden'
        )}
      >
        {isTableDragHighlight && (
          <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-500 rounded-full z-10" />
        )}
        <button
          type="button"
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
          {...attributes}
          {...listeners}
          tabIndex={0}
        >
          <Bars3Icon className="h-4 w-4" />
        </button>
        <span
          className={clsx(
            'flex-1 min-w-0 truncate select-none',
            visible ? 'text-gray-700' : 'text-gray-400'
          )}
          title={label}
        >
          {label}
        </span>
        <PinButton columnId={id} />
        <button
          type="button"
          onClick={() => onToggleVisibility(id)}
          className={clsx(
            'flex-shrink-0 rounded p-0.5 transition-colors',
            visible ? 'text-gray-500 hover:text-gray-700' : 'text-gray-300 hover:text-gray-500'
          )}
          title={visible ? 'Hide column' : 'Show column'}
          aria-label={visible ? 'Hide column' : 'Show column'}
        >
          {visible ? (
            <EyeIcon className="h-3.5 w-3.5" />
          ) : (
            <EyeSlashIcon className="h-3.5 w-3.5" />
          )}
        </button>
      </div>
    );
  };

  const Panel: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const columns = useColumnStore((state) => state.columns);
    const toggleColumnVisibility = useColumnStore((state) => state.toggleColumnVisibility);
    const resetColumns = useColumnStore((state) => state.resetColumns);
    const isColumnsDefault = useColumnStore((state) => state.isColumnsDefault);
    const pinnedLeft = useColumnStore((state) => state.pinnedLeft);
    const pinnedRight = useColumnStore((state) => state.pinnedRight);
    const showAllColumns = useColumnStore((s) => s.showAllColumns);
    const hideAllColumns = useColumnStore((s) => s.hideAllColumns);
    const { activeDragId, activeDragSource } = useCrossDnd();
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const displayColumns = useMemo(
      () => columns.filter((c) => c.id !== 'rowNumber'),
      [columns]
    );

    useEffect(() => {
      if (activeDragSource !== 'table' || !activeDragId || !scrollContainerRef.current) return;
      const sortableId = addPrefix(activeDragId);
      const container = scrollContainerRef.current;
      const row = container.querySelector(`[data-sortable-id="${sortableId}"]`) as HTMLElement | null;
      if (row) {
        row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, [activeDragSource, activeDragId, columns]);

    const { sidebarOverColumnId, sidebarInsertBefore } = useCrossDnd();
    const [indicatorTop, setIndicatorTop] = useState<number | null>(null);
    useEffect(() => {
      if (!sidebarOverColumnId || !activeDragId || sidebarOverColumnId === activeDragId || !scrollContainerRef.current) {
        setIndicatorTop(null);
        return;
      }
      const container = scrollContainerRef.current;
      const rows = container.querySelectorAll('[data-sortable-id]');
      for (const row of rows) {
        if ((row as HTMLElement).dataset.sortableId === sidebarOverColumnId) {
          const rowEl = row as HTMLElement;
          setIndicatorTop(sidebarInsertBefore ? rowEl.offsetTop : rowEl.offsetTop + rowEl.offsetHeight);
          return;
        }
      }
      setIndicatorTop(null);
    }, [sidebarOverColumnId, activeDragId, sidebarInsertBefore]);

    const pinnedLeftSet = useMemo(() => new Set(pinnedLeft), [pinnedLeft]);
    const pinnedRightSet = useMemo(() => new Set(pinnedRight), [pinnedRight]);

    const { pinnedLeftCols, pinnedRightCols, unpinnedCols } = useMemo(() => {
      const left: ColumnConfig[] = [];
      const right: ColumnConfig[] = [];
      const rest: ColumnConfig[] = [];
      for (const col of displayColumns) {
        if (pinnedLeftSet.has(col.id)) left.push(col);
        else if (pinnedRightSet.has(col.id)) right.push(col);
        else rest.push(col);
      }
      return { pinnedLeftCols: left, pinnedRightCols: right, unpinnedCols: rest };
    }, [displayColumns, pinnedLeftSet, pinnedRightSet]);

    const query = searchTerm.trim().toLowerCase();
    const matchesSearch = useCallback(
      (label: string): boolean => {
        if (!query) return true;
        return label.toLowerCase().includes(query);
      },
      [query]
    );

    const hasPinnedGroups = pinnedLeftCols.length > 0 || pinnedRightCols.length > 0;

    const renderColumnRow = (col: ColumnConfig) => (
      <SortableColumnRow
        key={col.id}
        id={col.id}
        label={col.label}
        visible={col.visible}
        onToggleVisibility={toggleColumnVisibility}
        isHidden={!matchesSearch(col.label)}
        scrollContainerRef={scrollContainerRef}
      />
    );

    return (
      <div className="flex flex-1 flex-col gap-2 min-h-0">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search columns..."
            className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-3 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={showAllColumns}
            className="text-xs font-medium text-blue-600 hover:text-blue-800"
          >
            Show all
          </button>
          <span className="text-gray-300">|</span>
          <button
            type="button"
            onClick={hideAllColumns}
            className="text-xs font-medium text-blue-600 hover:text-blue-800"
          >
            Hide all
          </button>
          {!isColumnsDefault() && (
            <>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={resetColumns}
                className="text-xs font-medium text-blue-600 hover:text-blue-800"
              >
                Reset
              </button>
            </>
          )}
        </div>
        <div ref={scrollContainerRef} className="-mr-6 flex-1 overflow-y-auto space-y-0.5 pr-6 relative">
          {indicatorTop !== null && (
            <div
              className="absolute left-0 right-6 h-0.5 bg-blue-500 rounded-full z-10 pointer-events-none transition-[top] duration-75 ease-out"
              style={{ top: `${indicatorTop}px` }}
            />
          )}
          <SortableContext
            items={displayColumns.map((c) => addPrefix(c.id))}
            strategy={noDisplacementStrategy}
          >
            {hasPinnedGroups ? (
              <div className="space-y-3">
                <ColumnGroup label="Pinned Left" visible={pinnedLeftCols.length > 0}>
                  {pinnedLeftCols.map(renderColumnRow)}
                </ColumnGroup>
                <ColumnGroup label="Pinned Right" visible={pinnedRightCols.length > 0}>
                  {pinnedRightCols.map(renderColumnRow)}
                </ColumnGroup>
                <ColumnGroup label="Columns" visible={unpinnedCols.length > 0}>
                  {unpinnedCols.map(renderColumnRow)}
                </ColumnGroup>
              </div>
            ) : (
              displayColumns.map(renderColumnRow)
            )}
          </SortableContext>
        </div>
      </div>
    );
  };

  return Panel;
}
